/**
 * Felanmäl on the guest page (#1650): description, priority, one of the
 * order's linked components or "Annat", and up to 10 photos. Sent through
 * guestApi (the guest has no database access); the guest cannot edit or
 * delete a report afterwards.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { GuestIssueReport } from '../hooks/useGuestReportIssue';
import { WORK_ORDER_LABELS as L } from '../../WorkOrders/workOrderSections';

const MAX_PHOTOS = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_DESCRIPTION = 2000;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

const PRIORITIES: { value: GuestIssueReport['priority']; label: string }[] = [
    { value: 'low', label: 'Låg' },
    { value: 'medium', label: 'Medel' },
    { value: 'high', label: 'Hög' },
];

export interface GuestReportIssueModalProps {
    show: boolean;
    onHide: () => void;
    /** The order's linked components, in order */
    components: { key: string; name: string }[];
    submitting: boolean;
    onSubmit: (report: GuestIssueReport) => Promise<void>;
}

const GuestReportIssueModal: React.FC<GuestReportIssueModalProps> = ({ show, onHide, components, submitting, onSubmit }) => {
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<GuestIssueReport['priority']>('medium');
    const [componentKey, setComponentKey] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (show) {
            setDescription('');
            setPriority('medium');
            setComponentKey(components.length === 1 ? components[0].key : '');
            setFiles([]);
            setError(null);
        }
        // Reset only when the form opens; `components` may be a new array each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const addFiles = (list: FileList | null) => {
        if (!list) return;
        const picked = Array.from(list);
        if (files.length + picked.length > MAX_PHOTOS) {
            setError(`Max ${MAX_PHOTOS} bilder`);
            return;
        }
        const invalid = picked.find((f) => !ACCEPTED_TYPES.includes(f.type) || f.size > MAX_FILE_SIZE);
        if (invalid) {
            setError(ACCEPTED_TYPES.includes(invalid.type) ? `${invalid.name} är för stor (max 10 MB)` : 'Endast JPEG- och PNG-bilder');
            return;
        }
        setError(null);
        setFiles((prev) => [...prev, ...picked]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!description.trim()) {
            setError('Beskrivning krävs');
            return;
        }
        setError(null);
        try {
            await onSubmit({ description, priority, componentKey: componentKey || undefined, files });
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte skicka felanmälan');
        }
    };

    return (
        <Modal show={show} onHide={submitting ? undefined : onHide} centered>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title>{L.reportIssue}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                    <p className="text-muted small">
                        Hittade du något annat fel medan du arbetade? Det skickas till förvaltaren som en felanmälan.
                    </p>
                    <Form.Group className="mb-3" controlId="guest-report-description">
                        <Form.Label>Beskrivning *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={MAX_DESCRIPTION}
                            placeholder="Beskriv felet..."
                            disabled={submitting}
                        />
                        <Form.Text className="text-muted">{description.length}/{MAX_DESCRIPTION} tecken</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="guest-report-component">
                        <Form.Label>Komponent</Form.Label>
                        <Form.Select value={componentKey} onChange={(e) => setComponentKey(e.target.value)} disabled={submitting}>
                            {components.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                            <option value="">Annat</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="guest-report-priority">
                        <Form.Label>Prioritet</Form.Label>
                        <Form.Select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value as GuestIssueReport['priority'])}
                            disabled={submitting}
                        >
                            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="guest-report-photos">
                        <Form.Label>Bilder (valfritt, max {MAX_PHOTOS})</Form.Label>
                        <Form.Control
                            type="file"
                            accept={ACCEPTED_TYPES.join(',')}
                            multiple
                            disabled={submitting || files.length >= MAX_PHOTOS}
                            onChange={(e) => { addFiles((e.target as HTMLInputElement).files); (e.target as HTMLInputElement).value = ''; }}
                        />
                        {files.length > 0 && (
                            <ul className="list-unstyled small mt-2 mb-0">
                                {files.map((file, index) => (
                                    <li key={`${file.name}-${index}`} className="d-flex align-items-center justify-content-between gap-2">
                                        <span className="text-truncate">{file.name}</span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            disabled={submitting}
                                            onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                                        >
                                            Ta bort
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? <><Spinner size="sm" className="me-2" />Skickar...</> : 'Skicka'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default GuestReportIssueModal;
